import { Option } from '@storeblocks/radio';
import { CancelDateCategory } from 'Forms/CancelFormV2/cancelForm.constants';
import { CancelFormValues } from 'Forms/CancelFormV2/cancelForm.types';
import { ChangeFormValues } from 'Forms/ChangeForm/changeForm.types';
import { DateString } from 'Types/date.types';
import {
  formatDateToDDMMYYYY,
  getPreviousDayDate,
} from 'Utils/date/date.utils';
import { getAppText } from 'Utils/text';
import { isDefined } from 'Utils/type.utils';
import { CancelReasonAkaTravel } from './cancelAkaTravelForm.constants';
import { CancelAkaTravelFormValues } from './cancelAkaTravelForm.types';

export const shouldHaveFutureCancelDateAkaTravel = (
  cancelReason: unknown,
): boolean =>
  cancelReason === CancelReasonAkaTravel.INSURED_VIA_EMPLOYER ||
  cancelReason === CancelReasonAkaTravel.INSURED_VIA_OTHER_UNION ||
  cancelReason === CancelReasonAkaTravel.NOT_LONGER_IN_UNION ||
  cancelReason === CancelReasonAkaTravel.INSURED_VIA_PARTNER;

export const shouldShowFutureCancelAlert = (
  cancelReason: CancelReasonAkaTravel,
): boolean =>
  cancelReason === CancelReasonAkaTravel.INSURED_VIA_EMPLOYER ||
  cancelReason === CancelReasonAkaTravel.INSURED_VIA_OTHER_UNION ||
  cancelReason === CancelReasonAkaTravel.INSURED_VIA_PARTNER;

export const mapCancelDateCategory = (
  values: CancelAkaTravelFormValues,
): CancelDateCategory => {
  if (
    isDefined(values.cancelReason) &&
    shouldHaveFutureCancelDateAkaTravel(values.cancelReason)
  ) {
    return CancelDateCategory.FUTURE;
  }
  return CancelDateCategory.RENEWAL;
};

export const getCancelAkaTravelAlertTitle = (
  cancelReason?: CancelReasonAkaTravel,
): string => {
  if (!isDefined(cancelReason)) {
    return '';
  }
  if (shouldShowFutureCancelAlert(cancelReason)) {
    return getAppText('cancel.travel.cancelReason.alert.title.futureCancel');
  }
  return getAppText('cancel.travel.cancelReason.alert.title.renewalCancel');
};

export const getCancelAkaTravelAlertDescription = (
  cancelReason?: CancelReasonAkaTravel,
): string => {
  if (!isDefined(cancelReason)) {
    return '';
  }
  if (shouldShowFutureCancelAlert(cancelReason)) {
    return getAppText(
      'cancel.travel.cancelReason.alert.description.futureCancel',
    );
  }
  return getAppText(
    'cancel.travel.cancelReason.alert.description.renewalCancel',
  );
};

export const isCancelAkaTravelFormValues = (
  values: CancelFormValues | ChangeFormValues,
): values is CancelAkaTravelFormValues => {
  return (
    'futureCancelDate' in values &&
    (values.futureCancelDate === undefined ||
      values.futureCancelDate instanceof Date)
  );
};

export const getSubmitSuccessDescriptionTravel = (
  values: CancelFormValues | ChangeFormValues,
  renewalDate: string,
): string => {
  if (isCancelAkaTravelFormValues(values)) {
    if (
      isDefined(values.cancelReason) &&
      shouldHaveFutureCancelDateAkaTravel(values.cancelReason) &&
      isDefined(values.futureCancelDate)
    ) {
      const dayBefore = getPreviousDayDate(
        formatDateToDDMMYYYY(values.futureCancelDate),
      );

      return getAppText('cancel.travel.alert.submitSuccesOnDate.description', {
        insert: {
          dayBeforeCancelDate: dayBefore,
        },
      });
    }

    if (
      isDefined(values.cancelReason) &&
      values.cancelReason === CancelReasonAkaTravel.NOT_IN_NEED
    ) {
      const dayBefore = getPreviousDayDate(renewalDate as DateString);
      return getAppText(
        'cancel.travel.alert.submitSuccesOnRenewal.description',
        { insert: { dayBeforeRenewal: dayBefore } },
      );
    }
  }

  return '';
};

export const showFutureCancelDate = (
  values: CancelAkaTravelFormValues,
): boolean =>
  isDefined(values.cancelReason) &&
  shouldHaveFutureCancelDateAkaTravel(values.cancelReason);

export const getAkaTravelCancellationOptions = (): Option[] => [
  {
    label: getAppText('cancel.travel.cancelReason.option.employer'),
    value: CancelReasonAkaTravel.INSURED_VIA_EMPLOYER,
    id: 'cancelReason-1',
  },
  {
    label: getAppText('cancel.travel.cancelReason.option.notLongerInUnion'),
    value: CancelReasonAkaTravel.NOT_LONGER_IN_UNION,
    id: 'cancelReason-2',
  },
  {
    label: getAppText('cancel.travel.cancelReason.option.otherUnion'),
    value: CancelReasonAkaTravel.INSURED_VIA_OTHER_UNION,
    id: 'cancelReason-3',
  },
  {
    label: getAppText('cancel.travel.cancelReason.option.partner'),
    value: CancelReasonAkaTravel.INSURED_VIA_PARTNER,
    id: 'cancelReason-4',
  },
  {
    label: getAppText('cancel.travel.cancelReason.option.other'),
    value: CancelReasonAkaTravel.NOT_IN_NEED,
    id: 'cancelReason-5',
  },
];
